import { type Json } from '../core/index.js';
import { getClipboardData, setClipboardData } from './clipboard.js';
import {
  type FabricUtilsGetContactApi,
  type FabricUtilsGetContactsApi,
  getContact,
  getContacts,
  getContactsPermissionStatus,
} from './contact.js';
import {
  type FabricUtilsGetGravityApi,
  getGravityInfo,
} from './get-gravity-info.js';
import {
  type FabricUtilsGetInstalledAppsApi,
  getInstalledApps,
} from './get-installed-apps.js';
import {
  type FabricUtilsGetOutsidePathReadPermissionApi,
  type FabricUtilsGetOutsidePathReadPermissionStatusApi,
  getOutsidePathReadPermission,
  getOutsidePathReadPermissionStatus,
} from './get-outside-path-read-permission.js';
import type { FabricUtilsGetPermissionApi } from './get-permission.js';
import { getPermission } from './get-permission.js';
import { getRuntimeConfig } from './get-runtime-config.js';
import {
  type FabricGetSystemInfoCallbackData,
  getSystemInfo,
} from './get-system-info.js';
import {
  type FabricGetSystemInfoExtendCallbackData,
  getSystemInfoExtend,
} from './get-system-info-extend.js';
import {
  type FabricGetSystemRuntimeInfoApi,
  getSystemRuntimeInfo,
} from './get-system-runtime-info.js';
import {
  type FabricUtilsOpenPdfViewerApi,
  openPdfViewer,
} from './open-pdf-viewer.js';
import {
  type FabricUtilsOpenAppSettingsApi,
  openAppSettings,
} from './open-settings.js';
import { type FabricUtilsScanQrCodeApi, scanQrCode } from './scan-qrcode.js';
import {
  type FabricUtilsSendTextMsgApi,
  sendTextMsg,
} from './send-text-msg.js';
import {
  callPhoneNumber,
  type FabricUtilsCallPhoneNumberApi,
} from './telephone.js';

export type FabricUtilsApi = {
  /**
   * 选择通讯录, 会弹出通讯录界面, 让用户选择单个联系人
   */
  getContact: FabricUtilsGetContactApi;
  /**
   * 获取通讯录列表, 异步调用
   */
  getContacts: FabricUtilsGetContactsApi;
  /**
   * 检查当前是否有权限获取通讯录信息, 业务上可能在有权限再去获取, 避免权限弹窗干扰.
   */
  getContactsPermissionStatus: () => boolean;
  /**
   * 获取本机安装的app列表
   */
  getInstalledApps: FabricUtilsGetInstalledAppsApi;

  /**
   * 获取本地运行时配置的动态数据, 比如调试工具提供的`tagline`, 自定义`accessToken`
   */
  getRuntimeConfig: typeof getRuntimeConfig;

  /**
   * 获取系统信息, 使用同步实现来返回。
   */
  getSystemInfo: () => FabricGetSystemInfoCallbackData;

  /**
   * 获取系统扩展信息, 使用同步实现来返回, 增强设备端信息获取. 有合规风险
   */
  getSystemInfoExtend: () => FabricGetSystemInfoExtendCallbackData;

  /**
   * 异步获取当前设备需要动态获取权限的API
   */
  getSystemRuntimeInfo: FabricGetSystemRuntimeInfoApi;

  /**
   * 获取重力感应毁掉数据, 会使用同步实现来返回。
   */
  getGravityInfo: FabricUtilsGetGravityApi;
  /**
   * 设置系统剪贴板的内容。调用成功后，会弹出 toast 提示"内容已复制"，持续 1.5s
   */
  setClipboardData: (data: Json) => Promise<boolean>;
  /**
   * 获取系统剪贴板的内容
   */
  getClipboardData: <R extends Json>() => Promise<R>;
  /**
   * 打开PDF文件预览
   */
  openPdfViewer: FabricUtilsOpenPdfViewerApi;
  /**
   * 扫描二维码
   */
  scanQrCode: FabricUtilsScanQrCodeApi;
  /**
   * 拨打手机号码
   */
  callPhoneNumber: FabricUtilsCallPhoneNumberApi;

  /**
   * 打开应用设置面板.
   */
  openAppSettings: FabricUtilsOpenAppSettingsApi;

  /**
   * 打开发短信应用, 并初始化接受者电话, 短信内容
   */
  sendTextMsg: FabricUtilsSendTextMsgApi;

  /**
   * Android专用，异步调用
   * 向用户索要APP站外目录读取权限
   */
  getOutsidePathReadPermissionStatus: FabricUtilsGetOutsidePathReadPermissionStatusApi;
  getOutsidePathReadPermission: FabricUtilsGetOutsidePathReadPermissionApi;

  /**
   * 获取权限
   */
  getPermission: FabricUtilsGetPermissionApi;
};

export const fabricUtils: FabricUtilsApi = {
  scanQrCode,
  openPdfViewer,
  callPhoneNumber,
  getRuntimeConfig,
  getSystemInfo,
  getSystemRuntimeInfo,
  getSystemInfoExtend,
  getGravityInfo,
  setClipboardData,
  getClipboardData,
  getInstalledApps,
  getContact,
  getContacts,
  getContactsPermissionStatus,
  openAppSettings,
  sendTextMsg,
  getOutsidePathReadPermissionStatus,
  getOutsidePathReadPermission,
  getPermission,
};
